import { Row } from 'react-table';
import { IDataProps } from './../types';
/**
 * @file
 * Collection of useful helper functions.
 */

import get from 'lodash/get';
import once from 'lodash/once';

import snakeCase from 'lodash/snakeCase';

import { parseFromTimeZone } from 'date-fns-timezone/dist/parseFromTimeZone';
import format from 'date-fns/format';
import isAfter from 'date-fns/isAfter';
import parseISO from 'date-fns/parseISO';
import subMilliseconds from 'date-fns/subMilliseconds';

// Utils
import { getSponsorsByAnglerId } from '../blocks/BasstrakkLeaderboard/processing';

// Config
import {
  CATCH_TIMESPAN,
  GLOBAL_POLLING_RATE,
  SHARED_REFRESH_RATE,
  TIME_FORMAT,
} from '../config';

// Types
import { ISponsors } from '../blocks/BasstrakkLeaderboard/types';
import {
  AnglerKeyToMatch,
  CutlineRow,
  IBasstrakkAngler,
  IBasstrakkCatch,
  ICutline,
  IHighlighted,
  RefreshRate,
  TournamentClass,
  TournamentType,
} from '../types';

// Constants
const ORDINAL_SUFFIX = ['th', 'st', 'nd', 'rd'];

/**
 * Checks if the row (Angler) has a "New" catch and has sponsor logos.
 */
export const hasSponsors = (row: Row<IDataProps>, pollsLeft: number) => {
  return (
    pollsLeft &&
    row.original.sponsorLogos &&
    row.original.sponsorLogos.length
  );
};

/**
 * Get information needed for the Activity catch cell
 *
 * @param lastCatch
 * @param row
 */
export const activityCellInfo = (pollsLeft: number | null) => {
  // A "poll" is equal to 25 seconds says the ingest.
  // PollsLeft starts at 30, 30 - (120/25) = 25
  // @see ingest/src/server.js
  const threeMinutes = 25;
  let catchText = 'Last Catch';
  let catchClass = '';
  if (pollsLeft && pollsLeft > threeMinutes) {
    catchText = 'New Catch';
    catchClass = 'new-catch';
  }

  return { catchText, catchClass };
};

/**
 * Pass a comma separated list of class names and this function will join them.
 *
 * A class can be the result of a conditional as well:
 * Example:
 *   // If the activeAngler is equal to the FISHERMENID in the row it will
 *   // apply the 'active-angler' class.
 *   activeAngler === row.original.FISHERMENID && 'active-angler'
 */
export const classList = (...classes: Array<string | boolean>) => {
  return classes.filter(item => !!item).join(' ');
};

// Check for mobile
export const isMobile = once(
  () => window.matchMedia('only screen and (max-width: 760px)').matches
);

/**
 * Format money with commas in thousands place. No cents!
 * @param {number} amount - Amount in full dollars (no fractions)
 * @param {boolean} sign - Include $
 */
export const formatMoney = (amount: number, sign: boolean = true) =>
  [...[sign ? '$' : ''], amount.toLocaleString()].join('');

/**
 * Converts text into computer-friendly labels.
 * @param text - "dirty" string that is converted to safe string
 */
export const machineName = (text: string): string => snakeCase(text);

/**
 * Takes a string and gets the object property to match
 * @param {object} obj - e.g. {'foo': {'bar': {'baz': "Value"}}};
 * @param {string} lookup - e.g. 'foo.bar.baz'
 */
interface IObj {
  [key: string]: IObj | any;
}
export const objectStringReference = (obj: IObj, lookup: string): IObj | any =>
  get(obj, lookup);

/**
 * Converts ounces to a standard format (with lb and oz).
 * @TODO: use a lib for conversion?
 *
 * @param {number} ounces - in ounces
 * @param {boolean} units - include lb/oz strings
 */
export const prettifyOunces = (ounces?: number, units = true): string => {
  // If 0 or undefined, flat out yield 0.
  if (!ounces) {
    return '0';
  }

  const lb = Math.floor(ounces / 16);
  const oz = ounces % 16;

  // prettier-ignore
  return [
    ...([`${lb}${units ? 'lb' : ''}`]),
    ...([`${oz}${units ? 'oz' : ''}`])
  ].join(' - ');
};

/**
 * Parses the time and formats the time according to the given pattern.
 * @param {string} time - string (explodes in Safari) (i.e. 2018-08-26T15:16:46 or 01/06/2025 17:26:34)
 * @param {string} formatToken - patterns that determine the format (i.e. 'hh:mm aa')
 */

export const formatTime = (time: string, formatToken = TIME_FORMAT): string => {
  return format(Date.parse(time), formatToken);
}

/**
 * Util for refreshRate() below
 * @param rate
 */
const isDivisibleByGlobalRate = (rate: number) =>
  rate % GLOBAL_POLLING_RATE === 0;
/**
 * A block checks for new data on a rate that MUST be divisible by 5000
 * (GLOBAL_POLLING_RATE). The refresh rate can also be different for mobile.
 * @param rates - Tuple of desktop/default rate and mobile rate
 */
export const refreshRate = (rates: RefreshRate): RefreshRate => {
  if (rates.every(isDivisibleByGlobalRate)) {
    return rates;
  }
  // tslint:disable-next-line:no-console
  console.error(
    `Refresh rates must be divisible by ${GLOBAL_POLLING_RATE}. Falling back to default rates.`
  );
  return refreshRate(SHARED_REFRESH_RATE);
};

/**
 * Pull off the proper refresh rate for the current window size. Second value
 * in the tuple is mobile, first is desktop/default
 * Note: No array destructuring on function params due to lodash bug
 * @param {RefreshRate} rate - i.e. [30000, 60000]
 */
export const rateForSize = (rate: RefreshRate): number =>
  isMobile() ? rate[1] : rate[0];

/**
 * Convert numeric Tournament type enum (1|0) to string ('pro'|'co')
 * @param typeId
 */
export const tournamentTypeString = (typeId: TournamentType): string =>
  TournamentType[typeId].toLowerCase();

/**
 * Proper rank suffix for any number
 * Used for weigh-in
 * @param {number} position
 */
export const ordinal = (position: number) => {
  // Handles ranks > 100 reduces to double digit rank
  const modFixedRank = position % 100;
  // Suffix lookup voodoo
  const suffixLookup =
    ORDINAL_SUFFIX[(modFixedRank - 20) % 10] ||
    ORDINAL_SUFFIX[modFixedRank] ||
    ORDINAL_SUFFIX[0];

  return `${position}${suffixLookup}`;
};

/**
 * Get the epoch data from a catch's UID
 * @param catchItem
 */
export const catchEpochFromUID = (catchItem: IBasstrakkCatch) =>
  parseInt(catchItem.uid.split('-')[2], 10);

/**
 * Accepts an array of table rows, a function that returns a cutline table row,
 * and an array of tournament cutlines. Returns the initial rows with a cutline
 * row spliced in if cutlines exist.
 * @param initialRows
 * @param cutlineRow
 * @param cutlines
 */
export const checkForCutline = (
  initialRows: JSX.Element[],
  cutlineRow: CutlineRow,
  cutlines: ICutline[] | []
) => {
  // Accumulator for the number of cutlines already added to the rows array.
  let spliceOffset = 0;
  // Sort cutline from lowest to highest based on their row order.
  cutlines.sort((a, b) => a.cutlineValue - b.cutlineValue);

  cutlines.forEach((cutline, index) => {
    // Splice in each cutline if we have valid cutline values.
    if (cutline && cutline.cutlineValue) {
      // Only splice in cutline if the cutlineValue is less than or equal to the number of rows.
      if (initialRows.length - spliceOffset >= cutline.cutlineValue) {
        initialRows.splice(
          cutline.cutlineValue + spliceOffset,
          0,
          cutlineRow(cutline.cutlineText, `cutline-row-${index}`)
        );

        // Increment offset if new value was spliced in.
        spliceOffset += 1;
      }
    }
  });

  return initialRows;
};

// Get all anglers with kept catches in the last X minutes
export const anglerCatchesInLastTimespan = (
  catches: IBasstrakkCatch[],
  timezone: string
) => {
  if (!catches || !catches.length) {
    return [];
  }

  // Window of new catches, between now and CATCH_TIMESPAN ago
  const timeAgo = subMilliseconds(new Date(), CATCH_TIMESPAN);

  return (
    catches
      .filter(catchInfo => {
        // Immediately bail if not kept
        if (catchInfo.IsKept === 'N') {
          return false;
        }

        // Based on tournament timezone, what time was catch to site visitor?
        const catchInTimezone = parseFromTimeZone(catchInfo.Time, {
          timeZone: timezone,
        });

        // Is catch within window?
        return isAfter(catchInTimezone, timeAgo);
      })
      // BigBassLeaderboard uses uid for lookup, BasstrakkLeaderboard uses FISHERMENID.
      .map(({ AnglerId: FISHERMENID, uid }) => ({ FISHERMENID, uid }))
  );
};

// Get all anglers with kept catches in the last X minutes
export const anglerRowsInLastTimespan = (rows: IBasstrakkAngler[]) => {
  if (!rows || !rows.length) {
    return [];
  }

  return rows.filter(() => {
    return true;
  });
  // BigBassLeaderboard uses uid for lookup, BasstrakkLeaderboard uses FISHERMENID.
};

// Augment the incoming anglers to determine highlighted rows and add sponsors
// if applicable.
export const highlightedAnglers = (
  data: any,
  lastCatches: any[],
  // FISHERMENID or uid
  keyToMatch: AnglerKeyToMatch,
  // Optional sponsors param to add sponsor logos to data set.
  sponsors: ISponsors = {}
): Array<any & IHighlighted> => {
  // Check if row should be highlighted.
  const highlighted = data.map((item: any) => ({
    ...item,
    isHighlighted: lastCatches
      .map(lastCatch => lastCatch[keyToMatch])
      .includes(item[keyToMatch]),
  }));

  // If we have sponsors, check if each row has a sponsor and return.
  if (sponsors) {
    return highlighted.map((item: any) => ({
      ...item,
      sponsorLogos: getSponsorsByAnglerId(item.FISHERMENID, sponsors),
    }));
  }

  return highlighted;
};

// Add sponsors to corresponding rows.
// if applicable.
export const sponsorRows = (
  data: any,
  // Optional sponsors param to add sponsor logos to data set.
  sponsors: ISponsors = {}
): Array<any & IHighlighted> => {
  // If we have sponsors, check if each row has a sponsor and return.
  if (sponsors && data) {
    return data.map((item: any) => ({
      ...item,
      sponsorLogos: getSponsorsByAnglerId(item.FISHERMENID, sponsors),
    }));
  }

  return data;
};
